import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Toggle } from '../components/ui/Toggle';
import { Badge } from '../components/ui/Badge';
import { X, Star, Tag, Layers, Ruler, DollarSign, ImageOff, ChevronDown, ArrowLeft } from 'lucide-react';
type OptionType = 'Standard' | 'Upgrade' | 'Custom';
type OptionLevel = 0 | 1 | 2 | 3;
type Uom = 'EA' | 'LF' | 'SF' | 'SY' | 'CY';
const TYPE_OPTIONS: OptionType[] = ['Standard', 'Upgrade', 'Custom'];
const LEVEL_OPTIONS: OptionLevel[] = [0, 1, 2, 3];
const UOM_OPTIONS: {
  value: Uom;
  label: string;
}[] = [{
  value: 'EA',
  label: 'EA – Each'
}, {
  value: 'LF',
  label: 'LF – Linear Feet'
}, {
  value: 'SF',
  label: 'SF – Square Feet'
}, {
  value: 'SY',
  label: 'SY – Square Yards'
}, {
  value: 'CY',
  label: 'CY – Cubic Yards'
}];
const LEVEL_STYLES: Record<OptionLevel, {
  label: string;
  className: string;
}> = {
  0: {
    label: 'Level 0',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  1: {
    label: 'Level 1',
    className: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  2: {
    label: 'Level 2',
    className: 'bg-violet-50 text-violet-700 border-violet-200'
  },
  3: {
    label: 'Level 3',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  }
};
export function EditOptionModalPreview() {
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const bid = builderId || 'mattamy';
  // Form state (seeded to match the screenshot)
  const [title, setTitle] = useState('Sub-Zero 36" Built-In');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<OptionType>('Upgrade');
  const [level, setLevel] = useState<OptionLevel>(0);
  const [uom, setUom] = useState<Uom>('EA');
  const [basePrice, setBasePrice] = useState('14980');
  const [enabled, setEnabled] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [salesOffice, setSalesOffice] = useState(false);
  const [designCenter, setDesignCenter] = useState(true);
  const [productId, setProductId] = useState('APPBISZ36000');
  const levelStyle = LEVEL_STYLES[level];
  const isIncluded = type === 'Standard';
  return <div className="min-h-screen bg-slate-50 py-10 px-4" data-id="element-5063">
      <div className="max-w-4xl mx-auto mb-6 flex items-center justify-between" data-id="element-5064">
        <div data-id="element-5065">
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5067" />} onClick={() => navigate(`/${bid}/options`)} data-id="element-5066">
            Back to Options
          </Button>
          <h1 className="text-xl font-bold text-slate-900 mt-2" data-id="element-5068">
            Edit Option Modal — Revised Layout
          </h1>
          <p className="text-sm text-slate-500" data-id="element-5069">
            Preview of the proposed edit modal design
          </p>
        </div>
      </div>

      {/* The Modal */}
      <div className="max-w-xl mx-auto" data-id="element-5070">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-slate-200" data-id="element-5071">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100" data-id="element-5072">
            <div data-id="element-5073">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-5074">
                Edit Option
              </h2>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-5075">
                Customize this option for your community
              </p>
            </div>
            <button onClick={() => navigate(`/${bid}/options`)} className="text-slate-400 hover:text-slate-600 -mt-1 -mr-1 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-id="element-5076">
              <X className="h-4 w-4" data-id="element-5077" />
            </button>
          </div>

          {/* Identity Preview */}
          <div className="px-6 pt-5" data-id="element-5078">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-5079">
              <div className="w-14 h-14 bg-white border border-slate-200 rounded-md flex items-center justify-center flex-shrink-0" data-id="element-5080">
                <ImageOff className="h-5 w-5 text-slate-300" data-id="element-5081" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5082">
                <div className="flex items-center gap-2" data-id="element-5083">
                  <p className="font-semibold text-slate-900 truncate" data-id="element-5084">
                    {title || 'Untitled Option'}
                  </p>
                  {isIncluded ? <Badge variant="success" className="text-[11px] flex-shrink-0" data-id="element-5085">
                      <Star className="h-3 w-3 mr-1" data-id="element-5086" />
                      Included
                    </Badge> : <Badge variant="neutral" className="text-[11px] flex-shrink-0 bg-slate-100 text-slate-600 border-slate-200" data-id="element-5087">
                      Upgrade
                    </Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-5088">
                  Appliances · Refrigerators · {productId}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-6 max-h-[60vh] overflow-y-auto" data-id="element-5089">
            {/* ── BASICS ───────────────────────────────────── */}
            <Section title="Basics" data-id="element-5090">
              <Field label="Title" data-id="element-5091">
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-5092" />
              </Field>
              <Field label="Description" hint="Shown to buyers in the catalog" data-id="element-5093">
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Add a short description…" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white resize-none placeholder:text-slate-400" data-id="element-5094" />
              </Field>
            </Section>

            {/* ── CLASSIFICATION ────────────────────────────── */}
            <Section title="Classification" data-id="element-5095">
              <div className="grid grid-cols-3 gap-3" data-id="element-5096">
                <Field label="Type" icon={Tag} data-id="element-5097">
                  <select value={type} onChange={e => setType(e.target.value as OptionType)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-5098">
                    {TYPE_OPTIONS.map(t => <option key={t} value={t} data-id="element-5099">
                        {t}
                      </option>)}
                  </select>
                </Field>
                <Field label="Level" icon={Layers} data-id="element-5100">
                  <div className="relative" data-id="element-5101">
                    <select value={level} onChange={e => setLevel(parseInt(e.target.value, 10) as OptionLevel)} className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 font-medium ${levelStyle.className}`} data-id="element-5102">
                      {LEVEL_OPTIONS.map(l => <option key={l} value={l} data-id="element-5103">
                          Level {l}
                        </option>)}
                    </select>
                  </div>
                </Field>
                <Field label="Unit" icon={Ruler} data-id="element-5104">
                  <select value={uom} onChange={e => setUom(e.target.value as Uom)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-5105">
                    {UOM_OPTIONS.map(u => <option key={u.value} value={u.value} data-id="element-5106">
                        {u.value}
                      </option>)}
                  </select>
                </Field>
              </div>
            </Section>

            {/* ── PRICING ───────────────────────────────────── */}
            <Section title="Pricing" data-id="element-5107">
              <Field label="Base Price" data-id="element-5108">
                <div className="relative" data-id="element-5109">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-5110" />
                  <input type="number" value={basePrice} onChange={e => setBasePrice(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white" data-id="element-5111" />
                </div>
              </Field>
            </Section>

            {/* ── VISIBILITY ────────────────────────────────── */}
            <Section title="Visibility & Behavior" data-id="element-5112">
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-id="element-5113">
                <ToggleRow label="Enabled" description="Available for buyers to select" checked={enabled} onChange={setEnabled} data-id="element-5114" />
                <ToggleRow label="Default Option" description="Pre-selected for new buyers" checked={isDefault} onChange={setIsDefault} data-id="element-5115" />
                <ToggleRow label="Sales Office" description="Visible in the sales office view" checked={salesOffice} onChange={setSalesOffice} data-id="element-5116" />
                <ToggleRow label="Design Center" description="Visible in the design center view" checked={designCenter} onChange={setDesignCenter} data-id="element-5117" />
              </div>
            </Section>

            {/* ── REFERENCE ─────────────────────────────────── */}
            <Section title="Reference" data-id="element-5118">
              <Field label="Product ID" hint="Internal catalog identifier" data-id="element-5119">
                <input type="text" value={productId} onChange={e => setProductId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400 bg-white font-mono text-xs" data-id="element-5120" />
              </Field>
            </Section>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50" data-id="element-5121">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/${bid}/options`)} data-id="element-5122">
              Cancel
            </Button>
            <Button variant="primary" size="sm" data-id="element-5123">
              Update
            </Button>
          </div>
        </div>

        {/* Annotation */}
        <div className="mt-6 p-4 bg-white rounded-lg border border-slate-200 text-sm text-slate-600 space-y-2" data-id="element-5124">
          <p className="font-semibold text-slate-900" data-id="element-5125">What changed</p>
          <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-5126">
            <li data-id="element-5127">
              <strong data-id="element-5128">Logical grouping</strong>: Basics → Classification →
              Pricing → Visibility → Reference
            </li>
            <li data-id="element-5129">
              <strong data-id="element-5130">Section eyebrows</strong> replace heavy per-field bold
              labels (lighter visual weight)
            </li>
            <li data-id="element-5131">
              <strong data-id="element-5132">Type / Level / UoM</strong> consolidated into one 3-column
              row instead of stacked rows
            </li>
            <li data-id="element-5133">
              <strong data-id="element-5134">Toggle stack</strong>: all 4 toggles in one bordered card
              with consistent row pattern (label, helper, toggle)
            </li>
            <li data-id="element-5135">
              <strong data-id="element-5136">Level color-coded</strong> dropdown reinforces tier at a
              glance
            </li>
            <li data-id="element-5137">
              <strong data-id="element-5138">Identity preview</strong> shows live title + status badge
              + breadcrumb (replaces the dead "..." subtext)
            </li>
            <li data-id="element-5139">
              <strong data-id="element-5140">Product ID</strong> moved to "Reference" footer section
              (least-edited field, no longer interrupting the flow)
            </li>
          </ul>
        </div>
      </div>
    </div>;
}
// ─── Subcomponents ────────────────────────────────────────────────────────
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="space-y-3" data-id="element-5141">
      <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-5142">
        {title}
      </h3>
      <div className="space-y-3" data-id="element-5143">{children}</div>
    </div>;
}
function Field({
  label,
  hint,
  icon: Icon,
  children
}: {
  label: string;
  hint?: string;
  icon?: ComponentType<{
    className?: string;
  }>;
  children: React.ReactNode;
}) {
  return <div className="space-y-1.5" data-id="element-5144">
      <div className="flex items-center justify-between gap-2" data-id="element-5145">
        <label className="text-xs font-medium text-slate-700 flex items-center gap-1.5" data-id="element-5146">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" data-id="element-5147" />}
          {label}
        </label>
        {hint && <span className="text-[11px] text-slate-400" data-id="element-5148">{hint}</span>}
      </div>
      {children}
    </div>;
}
function ToggleRow({
  label,
  description,
  checked,
  onChange
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-center justify-between gap-4 px-4 py-3" data-id="element-5149">
      <div className="min-w-0" data-id="element-5150">
        <p className="text-sm font-medium text-slate-900" data-id="element-5151">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-5152">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} size="sm" data-id="element-5153" />
    </div>;
}